import Link from 'next/link';
import React, { FC } from 'react';

import { Badge, Box, Button, Group, Space, Table } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';

import { User } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { ModalDescription } from 'components/modals/ModalDescription';
import { ModalTitle } from 'components/modals/ModalTitle';
import { useCompanyProfile } from 'hooks/use-company-profile';

type Props = ContextModalProps<{
    companySuggestions: User['companySuggestions'];
}>;

const CompanySuggestionsModal: FC<Props> = ({ context, id, innerProps }) => {
    const { companySuggestions = [] } = innerProps;

    const close = () => {
        context.closeModal(id);
    };

    return (
        <Box>
            <Space h="xs" />
            <ModalTitle maw="80%" textWrap="balance" mx="auto">
                {companySuggestions.length === 1
                    ? 'Do you want to join this company profile?'
                    : 'Do you want to join one of these company profiles?'}
            </ModalTitle>
            <Space h="xs" />
            <ModalDescription ta="center">
                {companySuggestions.length === 1
                    ? 'Based on your email address and company name, we think you might be interested in joining this company profile.'
                    : 'Based on your email address and company name, we think you might be interested in joining these company profiles.'}
            </ModalDescription>
            <Space h="lg" />
            <Table withTableBorder style={{ borderRadius: '4px' }}>
                <Table.Tbody>
                    {companySuggestions.map((companySuggestion) => (
                        <CompanySuggestion
                            companySuggestion={companySuggestion}
                            close={close}
                            key={companySuggestion.company}
                        />
                    ))}
                </Table.Tbody>
            </Table>
            <Space h="xs" />
            <Button onClick={close} variant="subtle" fullWidth>
                Skip
            </Button>
        </Box>
    );
};

const CompanySuggestion: FC<{
    companySuggestion: {
        company: string;
        reason: string;
    };
    close: () => void;
}> = ({ companySuggestion, close }) => {
    const { company } = useCompanyProfile(companySuggestion.company);

    return company ? (
        <Table.Tr>
            <Table.Td p="xs">
                <Group
                    component={Link}
                    // @ts-ignore
                    href={CompanyHelpers.urls.requestAccess(company.slug)}
                    onClick={close}
                    justify="space-between"
                    style={{
                        color: 'var(--mantine-color-gray-7)',
                        textDecoration: 'none',
                    }}
                >
                    {company.name}
                    <Badge size="xs" radius="xs" variant="default" style={{ cursor: 'pointer' }}>
                        Request to Join
                    </Badge>
                </Group>
            </Table.Td>
        </Table.Tr>
    ) : null;
};

export { CompanySuggestionsModal };
